import { useState } from "react";
import { motion } from "framer-motion";
import { Bell, BellRing, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { unlockAudio } from "@/lib/notification-behavior";

function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as Mac; disambiguate via touch support.
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // `standalone` is a non-standard Safari-only flag, absent from lib.dom.
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia?.("(display-mode: standalone)").matches || nav.standalone === true;
}

interface Props {
  /** Called when an iOS user in a browser tab needs to install first. */
  onNeedsInstall?: () => void;
  className?: string;
}

/**
 * One-tap "Enable notifications" button.
 *
 * Tapping it calls Notification.requestPermission(), which is what makes the
 * browser show its own native permission dialog — Safari, Chrome, Firefox,
 * Opera, Edge and Samsung Internet all implement this same standard API, so a
 * single code path covers every browser. On grant we immediately create the
 * push subscription and persist it, so permission and delivery can't drift
 * apart.
 *
 * iOS caveat: Safari only exposes Notification/PushManager to a web app that
 * has been added to the Home Screen (iOS 16.4+). In a normal tab the API does
 * not exist at all, so we route the user to install first rather than showing
 * a button that cannot work.
 */
export function EnableNotificationsButton({ onNeedsInstall, className }: Props) {
  const { isSupported, permission, isSubscribed, subscribe, loading } = usePushNotifications();
  const [justEnabled, setJustEnabled] = useState(false);

  const iosNeedsInstall = isIosDevice() && !isStandalone();

  // Already on and working — show a calm confirmation instead of a CTA.
  if ((isSubscribed && permission === "granted") || justEnabled) {
    return (
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="flex items-center justify-center gap-2 py-3 text-sm font-medium text-primary"
      >
        <CheckCircle2 className="h-5 w-5" /> Notifications On
      </motion.div>
    );
  }

  const handleClick = async () => {
    // iOS in a browser tab: the Notification API isn't available at all until
    // the app is on the Home Screen, so send them to install first.
    if (iosNeedsInstall) {
      onNeedsInstall?.();
      return;
    }

    if (!isSupported) {
      toast.error("This browser doesn't support notifications.");
      return;
    }

    if (permission === "denied") {
      toast.error("Notifications are blocked", {
        description:
          "Open your browser's site settings for ilajly.com and set Notifications to Allow, then try again.",
        duration: 8000,
      });
      return;
    }

    // Runs inside a real user gesture — the only moment the browser lets us
    // resume the AudioContext, which is what makes alert sounds audible.
    unlockAudio();

    // Triggers the browser's own native permission dialog.
    const sub = await subscribe();
    if (sub) {
      setJustEnabled(true);
      toast.success("Notifications enabled", {
        description: "You'll be alerted about consultations, replies and your queue turn.",
      });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Button
        size="lg"
        variant="outline"
        className={
          className ??
          "w-full rounded-xl border-2 border-primary py-6 text-base text-primary hover:bg-primary/5"
        }
        onClick={handleClick}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Enabling…
          </>
        ) : permission === "denied" ? (
          <>
            <Bell className="mr-2 h-5 w-5" />
            Notifications Blocked — Fix
          </>
        ) : (
          <>
            <BellRing className="mr-2 h-5 w-5" />
            Enable Notifications
          </>
        )}
      </Button>
    </motion.div>
  );
}
